<template>
  <div ref="containerRef" class="bg-white content-stretch flex flex-col gap-0 lg:gap-6 items-center justify-center py-0 lg:py-10 relative w-full">
    <div
      v-if="posts.length > 0"
      ref="viewportRef"
      class="relative h-[400px] w-full cursor-grab overflow-hidden touch-pan-y lg:h-96"
      :class="viewportClass"
      v-bind="swipeHandlers"
    >
      <div
        data-carousel-track
        class="absolute inset-0 size-full"
        :class="[trackClass, trackTransitionClass]"
        :style="carouselDragLayerStyle()"
      >
        <div
          v-for="virtualIndex in visibleIndices"
          :key="`post-${virtualIndex}`"
          class="absolute h-[400px] lg:h-96 lg:rounded-3xl"
          :class="slideTransitionClass"
          :style="{
            opacity: getPostOpacity(virtualIndex),
            pointerEvents: getPostPointerEvents(virtualIndex),
            width: `${slideWidth}px`,
            transform: `translate3d(${getPostTranslateX(virtualIndex)}px, 0, 0)`,
            left: '50%',
            marginLeft: `${-slideWidth / 2}px`
          }"
        >
          <!-- Mobile: image on top with overlapping text (Figma KLNP mobile) -->
          <div
            :class="[
              `bg-${postSecondaryClass(virtualIndex)}`,
              'relative lg:hidden h-full w-full overflow-hidden',
            ]"
          >
            <div class="absolute inset-x-0 top-0 h-[272px] overflow-hidden">
              <div
                v-if="getPostAtIndex(virtualIndex).image"
                class="absolute inset-0 bg-cover bg-center"
                :style="{ backgroundImage: `url(${getPostAtIndex(virtualIndex).image})` }"
              />
              <div
                class="absolute inset-0"
                :style="{ background: `linear-gradient(to bottom, rgba(${postCardRgb(virtualIndex)}, 0.4) 0%, rgba(${postCardRgb(virtualIndex)}, 0.85) 30%, rgb(${postCardRgb(virtualIndex)}) 55%)` }"
              />
            </div>

            <div
              :class="[
                'absolute inset-x-0 top-[72px] bottom-0 flex flex-col gap-3 px-4 pb-6',
                showPostButton(virtualIndex) ? '' : 'justify-end',
              ]"
            >
              <div class="flex min-h-0 flex-col gap-1 text-neutral-900">
                <p class="text-[14px] font-semibold uppercase leading-5 tracking-[1px] font-sans">
                  {{ getPostAtIndex(virtualIndex).category }}
                </p>
                <p class="text-[28px] font-medium leading-9 tracking-[-0.56px] font-sans">
                  {{ getPostAtIndex(virtualIndex).title }}
                </p>
              </div>

              <p class="min-h-0 text-[18px] font-medium leading-7 text-neutral-900 font-sans line-clamp-3">
                {{ getPostAtIndex(virtualIndex).description }}
              </p>

              <a
                v-if="showPostButton(virtualIndex) && getPostAtIndex(virtualIndex).buttonLink"
                :href="getPostAtIndex(virtualIndex).buttonLink"
                :class="[
                  'mt-auto flex h-12 min-h-12 shrink-0 w-full items-center justify-center rounded-lg border border-gray-200 bg-white text-[#374151] text-[16px] font-medium font-sans no-underline',
                ]"
              >
                {{ getPostAtIndex(virtualIndex).buttonText }}
              </a>
              <div
                v-else-if="showPostButton(virtualIndex) && getPostAtIndex(virtualIndex).buttonText"
                :class="[
                  'mt-auto flex h-12 min-h-12 shrink-0 w-full items-center justify-center rounded-lg border border-gray-200 bg-white text-[#374151] text-[16px] font-medium font-sans',
                ]"
              >
                {{ getPostAtIndex(virtualIndex).buttonText }}
              </div>
            </div>
          </div>

          <!-- Desktop: horizontal gradient card -->
          <div
            :class="[
              `bg-${postSecondaryClass(virtualIndex)}`,
              'hidden lg:block rounded-3xl h-full w-full relative overflow-hidden',
            ]"
          >
            <div class="flex flex-col items-start gap-6 pl-16 pt-16 max-w-2xl z-10 relative h-full">
              <div class="content-stretch flex flex-col items-start leading-none not-italic relative shrink-0 text-neutral-900 w-full">
                <div class="flex flex-col justify-center relative shrink-0 text-sm tracking-tight uppercase font-sans font-semibold">
                  <p class="leading-5 text-nowrap">{{ getPostAtIndex(virtualIndex).category }}</p>
                </div>
                <div class="flex flex-col justify-center relative shrink-0 text-4xl tracking-tighter font-sans font-medium">
                  <p class="leading-12 text-nowrap">{{ getPostAtIndex(virtualIndex).title }}</p>
                </div>
              </div>

              <div class="flex flex-col justify-center leading-none min-w-full not-italic relative shrink-0 text-lg text-neutral-900 w-min font-sans font-medium">
                <p class="leading-7">{{ getPostAtIndex(virtualIndex).description }}</p>
              </div>

              <a
                v-if="showPostButton(virtualIndex) && getPostAtIndex(virtualIndex).buttonLink"
                :href="getPostAtIndex(virtualIndex).buttonLink"
                :class="[
                  'bg-white border border-gray-200 border-solid content-stretch flex gap-2 h-12 items-center justify-center overflow-clip px-5 py-3.5 relative rounded-lg shrink-0 cursor-pointer hover:opacity-90 transition-opacity no-underline',
                ]"
              >
                <div class="flex flex-col justify-center leading-none not-italic relative shrink-0 text-[#374151] text-base text-center text-nowrap font-sans font-medium">
                  <p class="leading-5">{{ getPostAtIndex(virtualIndex).buttonText }}</p>
                </div>
              </a>
              <div
                v-else-if="showPostButton(virtualIndex) && getPostAtIndex(virtualIndex).buttonText"
                :class="[
                  'bg-white border border-gray-200 border-solid content-stretch flex gap-2 h-12 items-center justify-center overflow-clip px-5 py-3.5 relative rounded-lg shrink-0',
                ]"
              >
                <div class="flex flex-col justify-center leading-none not-italic relative shrink-0 text-[#374151] text-base text-center text-nowrap font-sans font-medium">
                  <p class="leading-5">{{ getPostAtIndex(virtualIndex).buttonText }}</p>
                </div>
              </div>
            </div>

            <div
              class="a11y-keep-bg flex items-center w-full h-full absolute top-0 left-0"
              :style="getPostAtIndex(virtualIndex).image ? {
                backgroundImage: `linear-gradient(90deg, rgba(${postCardRgb(virtualIndex)}, 1) 56%, rgba(${postCardRgb(virtualIndex)}, 0.8) 64%, rgba(${postCardRgb(virtualIndex)}, 0) 85%), url(${getPostAtIndex(virtualIndex).image})`,
                backgroundSize: 'auto 120%',
                backgroundPosition: 'right center',
                backgroundRepeat: 'no-repeat',
              } : {}"
            />
          </div>
        </div>
      </div>
    </div>

    <div v-else class="relative w-full h-[400px] lg:h-96 flex items-center justify-center text-gray-500">
      No posts available
    </div>

    <div v-if="posts.length > 0" class="w-full max-w-[1200px] px-4 py-6 lg:px-0 lg:py-0">
      <!-- Mobile: dots left, arrows right — Figma KLNP mobile (44px row, 16px inset, 24px vertical padding) -->
      <div class="flex lg:hidden h-11 w-full items-center justify-between">
        <CarouselDots
          :count="posts.length"
          :active-index="getCurrentDisplayIndex"
          :dot-size="8"
          :gap="12"
        >
          <template #default="{ index, active }">
            <button
              type="button"
              class="size-2 shrink-0 cursor-pointer rounded-full border-0 p-0 transition-colors duration-300 ease-in-out"
              :style="{ backgroundColor: active ? '#262626' : '#d1d5db' }"
              :aria-label="t('carousel.goto_slide', { n: index + 1 })"
              @click="goToSlide(index)"
            />
          </template>
        </CarouselDots>

        <div class="flex items-center gap-3">
          <button
            type="button"
            class="flex size-11 shrink-0 items-center justify-center rounded-full border bg-white cursor-pointer transition-opacity hover:opacity-80 p-0"
            :class="accentBorderClass"
            :aria-label="t('carousel.prev_slide')"
            @click="prevSlide"
          >
            <svg class="size-5" :class="accentTextClass" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
            </svg>
          </button>

          <button
            type="button"
            class="flex size-11 shrink-0 items-center justify-center rounded-full border bg-white cursor-pointer transition-opacity hover:opacity-80 p-0"
            :class="accentBorderClass"
            :aria-label="t('carousel.next_slide')"
            @click="nextSlide"
          >
            <svg class="size-5" :class="accentTextClass" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </div>
      </div>

      <!-- Desktop: centered dots, arrows on the right -->
      <div class="relative hidden lg:block h-11 w-full">
        <CarouselDots
          class="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2"
          :count="posts.length"
          :active-index="getCurrentDisplayIndex"
          :dot-size="6"
          :gap="8"
        >
          <template #default="{ index, active }">
            <button
              type="button"
              class="size-1.5 shrink-0 cursor-pointer rounded-full border-0 p-0 transition-all duration-300 ease-in-out"
              :class="active ? 'opacity-100 scale-125' : 'opacity-30 scale-100'"
              :style="{ backgroundColor: '#262626' }"
              :aria-label="t('carousel.goto_slide', { n: index + 1 })"
              @click="goToSlide(index)"
            />
          </template>
        </CarouselDots>

        <button
          type="button"
          class="absolute right-0 top-1/2 size-11 -translate-y-1/2 cursor-pointer"
          :aria-label="t('carousel.next_slide')"
          @click="nextSlide"
        >
          <div
            class="absolute left-0 top-0 flex size-11 items-center justify-center rounded-full border bg-white transition-opacity hover:opacity-80"
            :class="accentBorderClass"
          >
            <svg class="size-5" :class="accentTextClass" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
            </svg>
          </div>
        </button>

        <button
          type="button"
          class="absolute right-14 top-1/2 size-11 -translate-y-1/2 cursor-pointer"
          :aria-label="t('carousel.prev_slide')"
          @click="prevSlide"
        >
          <div
            class="absolute left-0 top-0 flex size-11 items-center justify-center rounded-full border bg-white transition-colors"
            :class="[accentBorderClass, accentHoverBgClass]"
          >
            <svg class="size-5" :class="accentTextClass" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
            </svg>
          </div>
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount } from 'vue'
import { useCarouselSwipe } from '@/utils/useCarouselSwipe.js'
import { carouselDragLayerStyle } from '@/utils/carouselTrack.js'
import {
  themeColorClassFor,
  themeColorRgbFor,
  themeSecondaryClassFor,
  themeCardColorRgbFor,
} from '@/utils/siteThemeColors'
import { useTranslations } from '@/utils/translations.js'
import CarouselDots from '@/Shared/CarouselDots.vue'

const { t } = useTranslations()

const props = defineProps({
  // eslint-disable-next-line vue/prop-name-casing
  site_context: {
    type: String,
    default: null,
  },
  posts: {
    type: Array,
    default: () => [],
  },
})

const POST_WIDTH = 1200
const POST_GAP = 32
const RENDER_BUFFER = 3
const MOBILE_BREAKPOINT = 1024

const currentIndex = ref(0)
const containerRef = ref(null)
const slideWidth = ref(
  typeof window !== 'undefined' ? Math.min(window.innerWidth, POST_WIDTH) : POST_WIDTH,
)
const isMobileViewport = ref(
  typeof window !== 'undefined' ? window.innerWidth < MOBILE_BREAKPOINT : false,
)
const transitionsEnabled = ref(false)

const {
  viewportRef,
  isDragging,
  viewportClass,
  trackClass,
  trackTransitionClass,
  swipeHandlers,
} = useCarouselSwipe({
  onNext: () => nextSlide(),
  onPrev: () => prevSlide(),
  enabled: () => props.posts.length > 1,
  canGoNext: () => props.posts.length > 1,
  canGoPrev: () => props.posts.length > 1,
})

const slideTransitionClass = computed(() => (
  transitionsEnabled.value && !isDragging.value
    ? 'transform-gpu backface-hidden transition-[transform,opacity] duration-[260ms] ease-[cubic-bezier(0.25,0.46,0.45,0.94)]'
    : 'transform-gpu backface-hidden'
))

let slideResizeObserver = null
let mobileMediaQuery = null

const measureSlideWidth = () => {
  const width = containerRef.value?.clientWidth || POST_WIDTH
  slideWidth.value = Math.min(width, POST_WIDTH)
}

const syncMobileViewport = () => {
  if (typeof window === 'undefined') return
  isMobileViewport.value = window.innerWidth < MOBILE_BREAKPOINT
}

onMounted(() => {
  syncMobileViewport()
  measureSlideWidth()

  requestAnimationFrame(() => {
    transitionsEnabled.value = true
  })

  if (typeof window !== 'undefined') {
    mobileMediaQuery = window.matchMedia(`(max-width: ${MOBILE_BREAKPOINT - 1}px)`)
    mobileMediaQuery.addEventListener('change', syncMobileViewport)
  }

  if (typeof ResizeObserver !== 'undefined' && containerRef.value) {
    slideResizeObserver = new ResizeObserver(() => {
      measureSlideWidth()
    })
    slideResizeObserver.observe(containerRef.value)
  } else if (typeof window !== 'undefined') {
    window.addEventListener('resize', measureSlideWidth)
  }
})

onBeforeUnmount(() => {
  if (slideResizeObserver) {
    slideResizeObserver.disconnect()
  } else if (typeof window !== 'undefined') {
    window.removeEventListener('resize', measureSlideWidth)
  }

  if (mobileMediaQuery) {
    mobileMediaQuery.removeEventListener('change', syncMobileViewport)
  }
})

const ACCENT_BY_SITE = {
  code_medical_center: {
    border: 'border-code-medical-center',
    text: 'text-code-medical-center',
    hover: 'hover:bg-code-medical-center-secondary',
  },
  infertility_treatment_clinic: {
    border: 'border-infertility-clinic',
    text: 'text-infertility-clinic',
    hover: 'hover:bg-infertility-clinic-secondary',
  },
  family_clinic: {
    border: 'border-family-clinic',
    text: 'text-family-clinic',
    hover: 'hover:bg-family-clinic-secondary',
  },
  institute: {
    border: 'border-institute',
    text: 'text-institute',
    hover: 'hover:bg-institute-secondary',
  },
}

function postSiteContext(virtualIndex) {
  return getPostAtIndex(virtualIndex).site_context || props.site_context
}

function postColorClass(virtualIndex) {
  return themeColorClassFor(postSiteContext(virtualIndex))
}

function postColorRgb(virtualIndex) {
  return themeColorRgbFor(postSiteContext(virtualIndex))
}

function postCardRgb(virtualIndex) {
  return themeCardColorRgbFor(postSiteContext(virtualIndex))
}

function postSecondaryClass(virtualIndex) {
  return themeSecondaryClassFor(postSiteContext(virtualIndex))
}

const getCurrentDisplayIndex = computed(() => {
  if (props.posts.length === 0) return 0
  let index = currentIndex.value % props.posts.length
  if (index < 0) index += props.posts.length
  return index
})

const activeSiteContext = computed(() => {
  const post = props.posts[getCurrentDisplayIndex.value]
  return post?.site_context || props.site_context || 'code_medical_center'
})

const accent = computed(() => (
  ACCENT_BY_SITE[activeSiteContext.value] || ACCENT_BY_SITE.code_medical_center
))
// Figma hero controls are neutral #262626 in every context (arrow ring +
// chevron, active indicator dot); only the hover fill keeps the per-clinic tint.
const accentBorderClass = 'border-[#262626]'
const accentTextClass = 'text-[#262626]'
const accentHoverBgClass = computed(() => accent.value.hover)

const visibleIndices = computed(() => {
  if (props.posts.length === 0) return []
  const indices = []
  for (let i = -RENDER_BUFFER; i <= RENDER_BUFFER; i++) {
    indices.push(currentIndex.value + i)
  }
  return indices
})

const getPostAtIndex = (virtualIndex) => {
  if (props.posts.length === 0) return {}

  let actualIndex = virtualIndex % props.posts.length
  if (actualIndex < 0) actualIndex += props.posts.length
  return props.posts[actualIndex]
}

const showPostButton = (virtualIndex) => {
  const post = getPostAtIndex(virtualIndex)
  if (post.showButton === false) return false
  return !!(post.buttonLink || post.buttonText)
}

const slideGap = computed(() => (isMobileViewport.value ? 0 : POST_GAP))

const getPostTranslateX = (index) => {
  const diff = index - currentIndex.value
  return diff * (slideWidth.value + slideGap.value)
}

const getPostOpacity = (index) => {
  const diff = Math.abs(index - currentIndex.value)
  if (isMobileViewport.value) return diff === 0 ? 1 : 0
  if (diff === 0) return 1
  if (diff === 1) return 0.4
  if (diff === 2) return 0.15
  return 0
}

const getPostPointerEvents = (index) => {
  const diff = Math.abs(index - currentIndex.value)
  if (isMobileViewport.value) return diff === 0 ? 'auto' : 'none'
  return diff === 0 ? 'auto' : 'none'
}

const nextSlide = () => { currentIndex.value++ }
const prevSlide = () => { currentIndex.value-- }

const goToSlide = (displayIndex) => {
  const currentDisplay = getCurrentDisplayIndex.value
  const totalPosts = props.posts.length

  let diff = displayIndex - currentDisplay
  if (Math.abs(diff) > totalPosts / 2) {
    diff += diff > 0 ? -totalPosts : totalPosts
  }
  currentIndex.value += diff
}
</script>
